import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { cn } from "../cn";

type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  duration: number;
}

type Options = Partial<Pick<ToastItem, "description" | "action" | "duration">>;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function push(kind: ToastKind, message: string, options: Options = {}) {
  seq += 1;
  const item: ToastItem = { id: seq, kind, message, duration: kind === "error" ? 7000 : 4000, ...options };
  items = [...items.slice(-3), item];
  emit();
  return item.id;
}

export function dismissToast(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

/** Plan: errors show a toast with "Try again"; form input is kept. */
export const toast = Object.assign((message: string, options?: Options) => push("info", message, options), {
  success: (message: string, options?: Options) => push("success", message, options),
  error: (message: string, options?: Options) => push("error", message, options),
  info: (message: string, options?: Options) => push("info", message, options),
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const icons = {
  success: <CircleCheck className="size-5 text-primary-fixed-dim" />,
  error: <CircleAlert className="size-5 text-[#ffb4ab]" />,
  info: <Info className="size-5 text-primary-fixed-dim" />,
};

function ToastView({ item }: { item: ToastItem }) {
  useEffect(() => {
    const timer = window.setTimeout(() => dismissToast(item.id), item.duration);
    return () => window.clearTimeout(timer);
  }, [item.id, item.duration]);
  return (
    <div
      role={item.kind === "error" ? "alert" : "status"}
      className="pointer-events-auto flex w-full animate-toast-in items-start gap-3 rounded-2xl bg-inverse-surface px-4 py-3 text-inverse-on-surface shadow-pop md:w-[380px]"
    >
      <span className="mt-0.5 shrink-0">{icons[item.kind]}</span>
      <div className="min-w-0 flex-1">
        <p className="text-label-lg">{item.message}</p>
        {item.description && <p className="mt-0.5 text-body-sm text-inverse-on-surface/75">{item.description}</p>}
      </div>
      {item.action && (
        <button
          type="button"
          onClick={() => {
            item.action?.onClick();
            dismissToast(item.id);
          }}
          className="shrink-0 rounded-lg px-2 py-1 text-label-md text-primary-fixed-dim hover:bg-white/10"
        >
          {item.action.label}
        </button>
      )}
      <button type="button" aria-label="Dismiss" onClick={() => dismissToast(item.id)} className="shrink-0 rounded-lg p-1 text-inverse-on-surface/60 hover:bg-white/10">
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Mount once near the root. Sits above the mobile bottom nav. */
export function Toaster({ className }: { className?: string }) {
  const list = useSyncExternalStore(subscribe, () => items, () => items);
  return (
    <div
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed inset-x-3 bottom-[calc(76px+env(safe-area-inset-bottom))] z-[80] flex flex-col items-center gap-2 md:inset-x-auto md:right-6 md:bottom-6 md:items-end",
        className,
      )}
    >
      {list.map((t) => (
        <ToastView key={t.id} item={t} />
      ))}
    </div>
  );
}
